import React, { useState, useEffect } from "react";
import axios from "axios";
import { API } from "../../App";
import { toast } from "sonner";
import { X, UserPlus, User, AlertTriangle, CheckCircle, Clock } from "lucide-react";
import { formatDate } from "../../utils/dateFormat";

/**
 * AssignKJDialog - Modal for assigning a KJ to a booking
 * Shows KJ availability for the booking date to prevent double-booking
 */
const AssignKJDialog = ({ booking, isOpen, onClose, onSuccess }) => {
  const [kjAvailability, setKjAvailability] = useState([]);
  const [selectedKJ, setSelectedKJ] = useState("");
  const [loading, setLoading] = useState(false);
  const [fetchingKJs, setFetchingKJs] = useState(false);

  useEffect(() => {
    if (isOpen && booking?.date) {
      fetchKJAvailability();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, booking?.date]);

  const fetchKJAvailability = async () => {
    setFetchingKJs(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/admin/kj-availability?date=${booking.date}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setKjAvailability(response.data.kj_availability || []);
    } catch (error) {
      // Fallback to regular KJ list if availability endpoint not available
      try {
        const token = localStorage.getItem('token');
        const fallbackResponse = await axios.get(`${API}/kj-list`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const kjList = fallbackResponse.data.kj_details || [];
        setKjAvailability(kjList.map(kj => ({
          ...kj,
          afternoon_available: true,
          evening_available: true,
          booked_slots: []
        })));
      } catch (e) {
        toast.error("Failed to fetch KJ list");
      }
    } finally {
      setFetchingKJs(false);
    }
  };

  const handleAssignKJ = async () => {
    if (!selectedKJ) {
      toast.error("Please select a KJ");
      return;
    }

    // Check if selected KJ is available for this slot
    const selectedKjData = kjAvailability.find(kj => kj.member_id === selectedKJ);
    const slotAvailable = booking.slot_type === 'afternoon' 
      ? selectedKjData?.afternoon_available 
      : selectedKjData?.evening_available;
    
    if (!slotAvailable) {
      const confirmed = window.confirm(
        `⚠️ ${selectedKjData?.name} is already booked for the ${booking.slot_type} slot on this date.\n\nAre you sure you want to assign them anyway?`
      );
      if (!confirmed) return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.put(
        `${API}/admin/bookings/${booking.booking_id}/assign-kj`,
        { kj_member_id: selectedKJ },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(`KJ assigned: ${response.data.kj_name}`);
      onSuccess && onSuccess(response.data);
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to assign KJ");
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen || !booking) return null;

  const getAvailabilityBadge = (kj) => {
    const slotAvailable = booking.slot_type === 'afternoon' 
      ? kj.afternoon_available 
      : kj.evening_available;
    
    if (kj.fully_booked) {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-red-100 text-red-700 rounded text-xs font-bold">
          <AlertTriangle size={10} />
          FULLY BOOKED
        </span>
      );
    }
    
    if (!slotAvailable) {
      const bookedSlot = kj.booked_slots?.find(s => s.slot_type === booking.slot_type);
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-amber-100 text-amber-700 rounded text-xs font-bold">
          <Clock size={10} />
          {booking.slot_type === 'afternoon' ? 'PM' : 'EVE'} BOOKED
          {bookedSlot && <span className="text-xs opacity-75">({bookedSlot.booking_code})</span>}
        </span>
      );
    }
    
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-green-100 text-green-700 rounded text-xs font-bold">
        <CheckCircle size={10} />
        AVAILABLE
      </span>
    );
  };

  return (
    <div 
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50"
      onClick={onClose}
      data-testid="assign-kj-modal"
    >
      <div 
        className="brutal-card max-w-md w-full relative max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute -top-3 -right-3 w-8 h-8 flex items-center justify-center bg-white text-gray-600 hover:text-black rounded-full text-xl font-bold shadow-lg border-2 border-black"
          aria-label="Close modal"
        >
          <X size={18} />
        </button>

        <div className="flex items-center gap-2 mb-4">
          <UserPlus className="text-purple-600" size={24} />
          <h2 className="text-xl font-syne font-bold">ASSIGN KJ</h2>
        </div>

        {/* Booking Info */}
        <div className="bg-purple-50 border-2 border-purple-300 p-3 mb-4 rounded">
          <div className="grid grid-cols-2 gap-2 text-sm">
            <div>
              <p className="text-xs font-bold text-purple-600">BOOKING CODE</p>
              <p className="font-mono font-bold">{booking.booking_code}</p>
            </div>
            <div>
              <p className="text-xs font-bold text-purple-600">DATE</p>
              <p className="font-mono">{formatDate(booking.date)}</p>
            </div>
            <div className="col-span-2">
              <p className="text-xs font-bold text-purple-600">RESIDENT</p>
              <p className="font-bold">{booking.member_name}</p>
              <p className="text-xs text-gray-500">{booking.member_email}</p>
            </div>
            <div className="col-span-2">
              <p className="text-xs font-bold text-purple-600">TIME SLOT</p>
              <p className="font-bold text-purple-700">
                {booking.slot_type === 'afternoon' ? '1:00 PM - 5:00 PM (Afternoon)' : '6:00 PM - 10:00 PM (Evening)'}
              </p>
            </div>
          </div>
        </div>

        {/* KJ Selection with Availability */}
        <div className="mb-4">
          <label className="block text-sm font-bold mb-2 flex items-center gap-1">
            <User size={14} /> Select KJ to Assign *
          </label>
          
          {fetchingKJs ? (
            <div className="text-center py-4 text-gray-500">
              <div className="animate-spin w-6 h-6 border-2 border-purple-500 border-t-transparent rounded-full mx-auto mb-2"></div>
              Loading KJ availability...
            </div>
          ) : (
            <div className="space-y-2 max-h-[200px] overflow-y-auto border border-gray-200 rounded p-2">
              {kjAvailability.length === 0 ? (
                <p className="text-gray-500 text-sm text-center py-2">No KJs available</p>
              ) : (
                kjAvailability.map(kj => {
                  const slotAvailable = booking.slot_type === 'afternoon' 
                    ? kj.afternoon_available 
                    : kj.evening_available;
                  
                  return (
                    <label 
                      key={kj.member_id}
                      className={`flex items-center justify-between p-2 rounded cursor-pointer transition-colors ${
                        selectedKJ === kj.member_id 
                          ? 'bg-purple-100 border-2 border-purple-500' 
                          : kj.fully_booked
                            ? 'bg-gray-50 opacity-60'
                            : !slotAvailable 
                              ? 'bg-amber-50 border border-amber-200'
                              : 'bg-gray-50 hover:bg-purple-50 border border-transparent'
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        <input
                          type="radio"
                          name="kj-selection"
                          value={kj.member_id}
                          checked={selectedKJ === kj.member_id}
                          onChange={(e) => setSelectedKJ(e.target.value)}
                          className="accent-purple-600"
                          disabled={kj.fully_booked}
                        />
                        <div>
                          <span className="font-bold">{kj.name}</span>
                          <span className="text-xs text-gray-500 ml-1">({kj.type})</span>
                        </div>
                      </div>
                      {getAvailabilityBadge(kj)}
                    </label>
                  );
                })
              )}
            </div>
          )}
          
          <p className="text-xs text-gray-500 mt-2">
            ✓ Shows real-time availability for {formatDate(booking.date)}
          </p>
        </div>

        {/* Info about KJ types */}
        <div className="bg-blue-50 border border-blue-200 p-3 rounded mb-4 text-xs">
          <p className="font-bold text-blue-800 mb-1">KJ Types:</p>
          <ul className="text-blue-700 space-y-0.5">
            <li><strong>External KJ:</strong> Earns $35 allowance per session</li>
            <li><strong>Member:</strong> RN Member (no allowance)</li>
          </ul>
        </div>

        {/* Warning if selected KJ is not available */}
        {selectedKJ && (() => {
          const selectedKjData = kjAvailability.find(kj => kj.member_id === selectedKJ);
          const slotAvailable = booking.slot_type === 'afternoon' 
            ? selectedKjData?.afternoon_available 
            : selectedKjData?.evening_available;
          
          if (!slotAvailable && selectedKjData) {
            return (
              <div className="bg-amber-50 border-2 border-amber-400 p-3 rounded mb-4">
                <p className="text-sm text-amber-800 flex items-start gap-2">
                  <AlertTriangle size={16} className="mt-0.5 shrink-0" />
                  <span>
                    <strong>{selectedKjData.name}</strong> is already booked for the {booking.slot_type} slot on this date. 
                    Assigning will result in double-booking.
                  </span>
                </p>
              </div>
            );
          }
          return null;
        })()}

        {/* Action Buttons */}
        <div className="flex gap-2">
          <button
            type="button"
            onClick={onClose}
            className="brutal-btn-outline flex-1 py-2"
            disabled={loading}
          >
            CANCEL
          </button>
          <button
            onClick={handleAssignKJ}
            className="brutal-btn-primary flex-1 py-2"
            disabled={loading || !selectedKJ}
            data-testid="confirm-assign-kj-btn"
          >
            {loading ? 'ASSIGNING...' : 'ASSIGN KJ'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AssignKJDialog;
